import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { geoNaturalEarth1, geoPath, geoGraticule10 } from "d3-geo";
import { feature } from "topojson-client";
import worldData from "world-atlas/countries-110m.json";
import { useLang } from "../context/LanguageContext";
import { EASE } from "./Reveal";
import { ORIGIN, DESTINATIONS, ALL_NODES } from "../data/network";

const W = 960;
const H = 520;
const K_MIN = 1;
const K_MAX = 6;

const GOLD = "#C9A96A";
const WARM = "rgba(226, 214, 194, 0.35)";

// robust module-level preparation with graceful fallback (no external services)
let project = null;
let countriesD = null;
let graticuleD = null;
try {
  const projection = geoNaturalEarth1().fitExtent([[6, 6], [W - 6, H - 6]], { type: "Sphere" });
  project = (lon, lat) => projection([lon, lat]);
  const pathGen = geoPath(projection);
  graticuleD = pathGen(geoGraticule10());
  countriesD = pathGen(feature(worldData, worldData.objects.countries));
} catch (e) {
  project = (lon, lat) => [((lon + 180) / 360) * W, ((90 - lat) / 180) * H];
}

const clampPan = (x, y, k) => [
  Math.min(0, Math.max(W * (1 - k), x)),
  Math.min(0, Math.max(H * (1 - k), y)),
];

const arc = (a, b) => {
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const dist = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return `M${a[0]},${a[1]} Q${mx},${my - dist * 0.22} ${b[0]},${b[1]}`;
};

const STATUS_COLORS = { origin: GOLD, priority: "#F3F3F0", target: "#8A8A86" };

export default function WorldMap() {
  const { t } = useLang();
  const reduced = useReducedMotion();
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  // ONE reliable parent-level observer drives every child animation —
  // fixes the frozen-route regression (per-element IntersectionObserver on
  // SVG children is unreliable; container-level useInView is proven here).
  const inView = useInView(wrapRef, { once: true, amount: 0.2 });

  const [selected, setSelected] = useState(ORIGIN.id);
  const [hovered, setHovered] = useState(null);
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const [smooth, setSmooth] = useState(false);

  // gesture state lives in refs; events are attached natively for reliability
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const dragged = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;

  const setViewSafe = (updater) => {
    setSmooth(false);
    setView((v) => {
      const n = updater(v);
      const [x, y] = clampPan(n.x, n.y, n.k);
      return { k: n.k, x, y };
    });
  };

  // wheel zoom (non-passive so the page does not scroll while zooming the map)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const toViewBox = (cx, cy) => {
      const rect = svg.getBoundingClientRect();
      return [(cx - rect.left) * (W / rect.width), (cy - rect.top) * (H / rect.height)];
    };
    const onWheel = (e) => {
      e.preventDefault();
      const [cx, cy] = toViewBox(e.clientX, e.clientY);
      setViewSafe((v) => {
        const k = Math.min(K_MAX, Math.max(K_MIN, v.k * Math.exp(-e.deltaY * 0.0014)));
        return { k, x: cx - (cx - v.x) * (k / v.k), y: cy - (cy - v.y) * (k / v.k) };
      });
    };
    const onDown = (e) => {
      if (e.target.closest("[data-zoom-ui]")) return;
      try { svg.setPointerCapture(e.pointerId); } catch (err) { /* non-active pointer */ }
      pointers.current.set(e.pointerId, [e.clientX, e.clientY]);
      dragged.current = 0;
      const pts = [...pointers.current.values()];
      gesture.current = pts.length >= 2
        ? { type: "pinch", dist: Math.hypot(pts[0][0] - pts[1][0], pts[0][1] - pts[1][1]) }
        : { type: "pan", last: pts[0] };
    };
    const onMove = (e) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, [e.clientX, e.clientY]);
      const rect = svg.getBoundingClientRect();
      const sx = W / rect.width;
      const sy = H / rect.height;
      const pts = [...pointers.current.values()];
      if (gesture.current?.type === "pinch" && pts.length >= 2) {
        const d2 = Math.hypot(pts[0][0] - pts[1][0], pts[0][1] - pts[1][1]);
        const [mx, my] = toViewBox((pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2);
        setViewSafe((v) => {
          const k = Math.min(K_MAX, Math.max(K_MIN, v.k * (d2 / (gesture.current.dist || d2))));
          gesture.current.dist = d2;
          dragged.current += 12;
          return { k, x: mx - (mx - v.x) * (k / v.k), y: my - (my - v.y) * (k / v.k) };
        });
      } else if (gesture.current?.type === "pan") {
        const dx = (e.clientX - gesture.current.last[0]) * sx;
        const dy = (e.clientY - gesture.current.last[1]) * sy;
        gesture.current.last = [e.clientX, e.clientY];
        dragged.current += Math.abs(dx) + Math.abs(dy);
        setViewSafe((v) => ({ k: v.k, x: v.x + dx, y: v.y + dy }));
      }
    };
    const onUp = (e) => {
      pointers.current.delete(e.pointerId);
      const pts = [...pointers.current.values()];
      gesture.current = pts.length >= 2
        ? { type: "pinch", dist: Math.hypot(pts[0][0] - pts[1][0], pts[0][1] - pts[1][1]) }
        : pts.length === 1
          ? { type: "pan", last: pts[0] }
          : null;
      // gesture finished — allow the next tap to select
      window.setTimeout(() => { dragged.current = 0; }, 0);
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    svg.addEventListener("pointerdown", onDown);
    svg.addEventListener("pointermove", onMove);
    svg.addEventListener("pointerup", onUp);
    svg.addEventListener("pointercancel", onUp);
    return () => {
      svg.removeEventListener("wheel", onWheel);
      svg.removeEventListener("pointerdown", onDown);
      svg.removeEventListener("pointermove", onMove);
      svg.removeEventListener("pointerup", onUp);
      svg.removeEventListener("pointercancel", onUp);
      pointers.current.clear();
      gesture.current = null;
    };
  }, []);

  const zoomBy = (f) => {
    setSmooth(true);
    setView((v) => {
      const k = Math.min(K_MAX, Math.max(K_MIN, v.k * f));
      const cx = W / 2;
      const cy = H / 2;
      return { k, x: cx - (cx - v.x) * (k / v.k), y: cy - (cy - v.y) * (k / v.k) };
    });
  };

  const reset = () => {
    setSmooth(true);
    setView({ k: 1, x: 0, y: 0 });
  };

  const selectNode = (id) => {
    if (dragged.current > 6) return; // ignore selection right after a drag
    setSelected(id);
  };

  const active = hovered || selected;
  const node = ALL_NODES.find((n) => n.id === active) || ALL_NODES[0];
  const info = t(`network.nodes.${node.id}`);

  const originXY = useMemo(() => project(ORIGIN.lon, ORIGIN.lat), []);
  const routes = useMemo(
    () => DESTINATIONS.map((d) => ({ ...d, d: arc(originXY, project(d.lon, d.lat)), xy: project(d.lon, d.lat) })),
    [originXY]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] border border-white/10" data-testid="world-map" ref={wrapRef}>
      <div className="relative bg-[#070809] p-2 md:p-4 map-rtl-lock">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto block select-none"
          style={{ touchAction: "none", cursor: view.k > 1 ? "grab" : "default" }}
          role="img"
          aria-label="VEYRONE global network map"
        >
          <g
            transform={`translate(${view.x},${view.y}) scale(${view.k})`}
            style={{ transition: smooth ? "transform 0.45s cubic-bezier(0.22,1,0.36,1)" : "none" }}
          >
            {/* graticule */}
            {graticuleD && (
              <motion.path
                d={graticuleD}
                fill="none"
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="0.5"
                style={{ pointerEvents: "none" }}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: reduced ? 0.01 : 1.2 }}
              />
            )}

            {/* countries — restrained outlines */}
            {countriesD && (
              <motion.path
                d={countriesD}
                fill="rgba(255,255,255,0.035)"
                stroke="rgba(255,255,255,0.20)"
                strokeWidth={0.7 / view.k}
                style={{ pointerEvents: "none" }}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: reduced ? 0.01 : 1.4, delay: reduced ? 0 : 0.15, ease: EASE }}
              />
            )}

            {/* animated routes + travelling light pulses */}
            {routes.map((r, i) => {
              const isSel = active === r.id;
              return (
                <g key={r.id} style={{ pointerEvents: "none" }}>
                  <motion.path
                    d={r.d}
                    fill="none"
                    stroke={isSel ? GOLD : WARM}
                    strokeWidth={(isSel ? 1.5 : 0.9) / view.k}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={inView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
                    transition={{
                      pathLength: { duration: reduced ? 0.01 : 1.7, delay: reduced ? 0 : 0.4 + i * 0.04, ease: EASE },
                      opacity: { duration: reduced ? 0.01 : 0.8, delay: reduced ? 0 : 0.4 + i * 0.04 },
                    }}
                  />
                  {!reduced && inView && (
                    <circle r={1.7 / view.k} fill={isSel ? GOLD : "#E8DCC8"} opacity={0.95}>
                      <animateMotion dur={`${7 + (i % 5)}s`} begin={`${i * 0.5}s`} repeatCount="indefinite" path={r.d} />
                    </circle>
                  )}
                </g>
              );
            })}

            {/* markers */}
            {ALL_NODES.map((n) => {
              const [x, y] = project(n.lon, n.lat);
              const isSel = active === n.id;
              const isOrigin = n.id === ORIGIN.id;
              const c = isOrigin ? GOLD : STATUS_COLORS[n.status];
              const label = t(`network.nodes.${n.id}.name`);
              return (
                <g
                  key={n.id}
                  transform={`translate(${x},${y})`}
                  style={{ cursor: "pointer" }}
                  data-testid={`map-node-${n.id}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`${label}, ${t(`network.nodes.${n.id}.country`)}`}
                  onClick={() => selectNode(n.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") selectNode(n.id); }}
                  onMouseEnter={() => setHovered(n.id)}
                  onMouseLeave={() => setHovered((h) => (h === n.id ? null : h))}
                >
                  <circle r={24 / view.k} fill="transparent" />
                  {isOrigin && <circle r={34 / view.k} fill="rgba(201,169,106,0.10)" />}
                  <motion.circle
                    r={isOrigin ? 7 / view.k : 4.5 / view.k}
                    fill={isOrigin ? GOLD : "#050607"}
                    stroke={c}
                    strokeWidth={1.6 / view.k}
                    initial={{ scale: 0 }}
                    animate={inView ? { scale: 1 } : { scale: 0 }}
                    transition={{ duration: reduced ? 0.01 : 0.5, delay: reduced ? 0 : 0.8, ease: EASE }}
                  />
                  {isOrigin && !reduced && (
                    <motion.circle
                      r={7 / view.k}
                      fill="none"
                      stroke={GOLD}
                      strokeWidth={1 / view.k}
                      animate={inView ? { scale: [1, 2.6], opacity: [0.8, 0] } : { opacity: 0 }}
                      transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}
                    />
                  )}
                  {(isOrigin || isSel || view.k >= 2) && (
                    <text
                      y={-14 / view.k}
                      textAnchor="middle"
                      fill={isSel || isOrigin ? "#F3F3F0" : "#B5B5B0"}
                      fontSize={11 / view.k}
                      fontFamily="IBM Plex Mono, monospace"
                      letterSpacing="1.6"
                      style={{ textTransform: "uppercase", pointerEvents: "none" }}
                    >
                      {label}
                    </text>
                  )}

                  {/* elegant SVG tooltip card on hover / selection */}
                  {active === n.id && !isOrigin && (
                    <g transform={`translate(${x},${y - 18}) scale(${1 / view.k})`} pointerEvents="none" data-testid="map-tooltip">
                      <rect
                        x={-(Math.max(info.name.length, info.country.length) * 3.6 + 14)}
                        y={-34}
                        width={Math.max(info.name.length, info.country.length) * 7.2 + 28}
                        height={34}
                        rx="2"
                        fill="#0D0E0F"
                        stroke="rgba(201,169,106,0.55)"
                        strokeWidth="0.8"
                      />
                      <text textAnchor="middle" y={-21} fill="#F3F3F0" fontSize="10.5" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">
                        {info.name}
                      </text>
                      <text textAnchor="middle" y={-9} fill="#8A8A86" fontSize="8.5" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.4">
                        {info.country}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* zoom controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-1.5" data-testid="map-zoom-controls" data-zoom-ui="1">
          <button onClick={() => zoomBy(1.5)} className="w-9 h-9 bg-[#0D0E0F]/90 border border-white/10 text-[#F3F3F0] mono text-[14px] hover:border-[#C9A96A]/60 transition-colors rounded-[2px]" data-testid="map-zoom-in" aria-label="Zoom in" title="Zoom in">+</button>
          <button onClick={() => zoomBy(1 / 1.5)} disabled={view.k <= K_MIN + 0.01} className="w-9 h-9 bg-[#0D0E0F]/90 border border-white/10 text-[#F3F3F0] mono text-[14px] hover:border-[#C9A96A]/60 transition-colors disabled:opacity-40 rounded-[2px]" data-testid="map-zoom-out" aria-label="Zoom out" title="Zoom out">−</button>
          <button onClick={reset} disabled={view.k <= K_MIN + 0.01} className="w-9 h-9 bg-[#0D0E0F]/90 border border-white/10 text-[#999995] mono text-[10px] hover:border-[#C9A96A]/60 hover:text-[#F3F3F0] transition-colors disabled:opacity-40 rounded-[2px]" data-testid="map-zoom-reset" aria-label="Reset view" title="Reset view">⟲</button>
        </div>

        {/* legend */}
        <div className="absolute bottom-4 left-4 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="map-legend" data-zoom-ui="1">
          {[["origin", "origin"], ["priority", "priority"], ["target", "target"]].map(([s, key]) => (
            <span key={s} className="flex items-center gap-2 bg-[#050607]/70 px-2 py-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLORS[s] }} />
              <span className="mono text-[9px] tracking-[0.18em] text-[#999995] uppercase">{t(`network.statuses.${key}`)}</span>
            </span>
          ))}
        </div>
      </div>

      {/* detail panel — the information card for the selected city */}
      <div className="border-t lg:border-t-0 lg:border-l border-white/10 bg-[#090A0B] p-6 md:p-8 flex flex-col" data-testid="map-detail-panel">
        <motion.div key={active} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
          <div className="eyebrow mb-6" style={{ color: STATUS_COLORS[node.id === ORIGIN.id ? "origin" : node.status] }}>
            {t(`network.statuses.${node.id === ORIGIN.id ? "origin" : node.status}`)}
          </div>
          <h3 className="font-display text-2xl text-[#F3F3F0] mb-1">{info.name}</h3>
          <div className="mono text-[11px] text-[#666663] tracking-[0.2em] mb-8">{info.country}</div>
          <dl className="space-y-5">
            {[
              ["city", info.name],
              ["country", info.country],
              ["status", t(`network.statuses.${node.id === ORIGIN.id ? "origin" : node.status}`)],
              ["note", info.note],
            ].map(([kk, v]) => (
              <div key={kk} className="border-t border-white/10 pt-3">
                <dt className="eyebrow !text-[9px] mb-1.5">{t(`network.labels.${kk}`)}</dt>
                <dd className="text-[13px] text-[#F3F3F0] leading-snug">{v}</dd>
              </div>
            ))}
          </dl>
        </motion.div>
        <div className="mt-auto pt-8">
          <p className="mono text-[9px] tracking-[0.16em] text-[#555654] uppercase leading-relaxed">
            {t("network.tapHint")}
          </p>
        </div>
      </div>
    </div>
  );
}
